import React, { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  Container,
  Divider,
  IconButton,
  Tooltip as MuiTooltip,
  Card,
  CardContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  PieChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
  Pie,
  Cell,
  CartesianGrid,
  Area,
  AreaChart,
} from "recharts";
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewsIcon,
  People as PeopleIcon,
  FileDownload as DownloadIcon,
  Public as PublicIcon,
  BarChart as BarChartIcon,
} from "@mui/icons-material";
import client from "../../../../../API";

// ==================== STYLED COMPONENTS ====================

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4),
  background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
  minHeight: "100vh",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const ModernCard = styled(Paper)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(20px)",
  borderRadius: "24px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s ease",
  overflow: "hidden",
  height: "100%",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
  },
}));

const GradientCard = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  borderRadius: "20px",
  padding: theme.spacing(3),
  color: "#fff",
  boxShadow: "0 12px 32px rgba(102, 126, 234, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 16px 48px rgba(102, 126, 234, 0.4)",
  },
}));

// Update these styled components to prevent prop forwarding:

const MetricCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'gradient',
})(({ gradient }) => ({
  padding: "24px",
  borderRadius: "16px",
  background: gradient || "linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))",
  border: "1px solid rgba(102, 126, 234, 0.2)",
  transition: "all 0.3s ease",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  },
}));

const StatBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'color',
})(({ theme, color }) => ({
  background: color || "linear-gradient(135deg, #667eea, #764ba2)",
  color: "#fff",
  fontWeight: 600,
  fontSize: "0.875rem",
  height: "auto",
  padding: "8px 16px",
  borderRadius: "12px",
  "& .MuiChip-label": {
    padding: 0,
  },
}));
const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: "3rem",
  fontWeight: 900,
  background: "linear-gradient(135deg, #667eea, #764ba2)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  marginBottom: theme.spacing(2),
  textAlign: "center",
  letterSpacing: "-0.02em",
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  color: "#2d3748",
  marginBottom: theme.spacing(3),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const StyledTable = styled(Table)(({ theme }) => ({
  "& .MuiTableHead-root": {
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    "& .MuiTableCell-root": {
      color: "#fff",
      fontWeight: 700,
      fontSize: "0.875rem",
      borderBottom: "none",
      padding: "16px",
    },
  },
  "& .MuiTableBody-root": {
    "& .MuiTableRow-root": {
      transition: "all 0.2s ease",
      "&:hover": {
        background: "rgba(102, 126, 234, 0.05)",
        transform: "scale(1.01)",
      },
      "&:nth-of-type(even)": {
        background: "rgba(0, 0, 0, 0.02)",
      },
    },
    "& .MuiTableCell-root": {
      padding: "16px",
      borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    },
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  borderRadius: "12px",
  padding: "12px 32px",
  color: "#fff",
  fontWeight: 600,
  textTransform: "none",
  fontSize: "1rem",
  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
  transition: "all 0.3s ease",
  border: "none",
  "&:hover": {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 12px 32px rgba(102, 126, 234, 0.4)",
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  padding: "24px",
  borderRadius: "16px",
  background: "rgba(255, 255, 255, 0.6)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  height: "100%",
}));

// ==================== HELPER FUNCTIONS ====================

const CHART_COLORS = [
  "#667eea", "#764ba2", "#f093fb", "#f5576c",
  "#4facfe", "#00f2fe", "#43e97b", "#38f9d7",
  "#ffecd2", "#fcb69f", "#ff9a56", "#ff6a88"
];

const METRIC_GRADIENTS = {
  purple: "linear-gradient(135deg, #667eea, #764ba2)",
  pink: "linear-gradient(135deg, #f093fb, #f5576c)",
  blue: "linear-gradient(135deg, #4facfe, #00f2fe)",
  green: "linear-gradient(135deg, #43e97b, #38f9d7)",
  orange: "linear-gradient(135deg, #fcb69f, #ff9a56)",
};

function findMainPlatform(creatorPublic = []) {
  if (!creatorPublic.length) return null;
  let mainIndex = 0;
  let maxFollowers = 0;
  for (let i = 0; i < creatorPublic.length; i++) {
    const followerCount = creatorPublic[i]?.followers ?? 0;
    if (followerCount > maxFollowers) {
      maxFollowers = followerCount;
      mainIndex = i;
    }
  }
  return creatorPublic[mainIndex];
}

function computePlatformSegments(platformData) {
  if (!platformData) {
    return {
      topCountry: { code: "N/A", value: 0 },
      topAge: { name: "N/A", value: 0 },
      malePercent: 0,
      femalePercent: 0,
    };
  }

  const countryMap = {};
  platformData.country_data?.forEach(({ code, value }) => {
    countryMap[code] = (countryMap[code] || 0) + value;
  });
  const countryArray = Object.entries(countryMap).map(([code, val]) => ({ code, val }));
  countryArray.sort((a, b) => b.val - a.val);
  const topCountry = countryArray[0] || { code: "N/A", val: 0 };

  const ageMap = {};
  platformData.audience_data?.forEach(({ age_range, value }) => {
    ageMap[age_range] = (ageMap[age_range] || 0) + value;
  });
  const ageArray = Object.entries(ageMap).map(([name, val]) => ({ name, val }));
  ageArray.sort((a, b) => b.val - a.val);
  const topAge = ageArray[0] || { name: "N/A", val: 0 };

  let maleVal = 0;
  let femaleVal = 0;
  platformData.audience_data?.forEach(({ gender, value }) => {
    if (gender?.toLowerCase() === "male") maleVal += value;
    else if (gender?.toLowerCase() === "female") femaleVal += value;
  });
  const total = maleVal + femaleVal;
  const malePercent = total ? Math.round((maleVal / total) * 100) : 0;
  const femalePercent = 100 - malePercent;

  return {
    topCountry: { code: topCountry.code, value: topCountry.val },
    topAge: { name: topAge.name, value: topAge.val },
    malePercent,
    femalePercent,
  };
}

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        sx={{
          p: 2,
          borderRadius: "12px",
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: "#2d3748" }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: entry.color, fontWeight: 600 }}
          >
            {`${entry.name}: ${typeof entry.value === "number" ? entry.value.toLocaleString() : entry.value}`}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

// ==================== MAIN COMPONENT ====================

const CampaignReportingPanel = ({
  costPerCreatorData = [],
  viewsByDayData = [],
  reachData = [],
  creatorIds = [],
}) => {
  const [creatorDetails, setCreatorDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Fetch creator details
  useEffect(() => {
    const fetchCreators = async () => {
      if (!creatorIds.length) return;
      setLoading(true);
      try {
        const response = await client.creators.fetchDetailsBatch(creatorIds, {
          include_public: true,
        });
        const details = response.creators.map((creator) => ({ creator }));
        setCreatorDetails(details);
      } catch (error) {
        console.error("Error fetching creator details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCreators();
  }, [creatorIds]);

  // Calculate summary metrics
  const summaryMetrics = {
    totalCost: costPerCreatorData.reduce((sum, item) => sum + (item.price || 0), 0),
    avgCostPerCreator: costPerCreatorData.length 
      ? costPerCreatorData.reduce((sum, item) => sum + (item.price || 0), 0) / costPerCreatorData.length 
      : 0,
    totalViews: viewsByDayData.reduce((sum, item) => sum + (item.views || 0), 0),
    totalReach: reachData.reduce((sum, item) => sum + (item.value || 0), 0),
    avgCPM: 0, // Calculate based on your data
  };

  // CSV Export
  const convertCreatorDetailsToCSV = () => {
    if (!creatorDetails.length) return "No data to export";
    const headers = [
      "Creator",
      "Location",
      "Platform",
      "Followers",
      "Avg Likes",
      "Avg Comments",
      "Engagement Rate",
      "Top Country",
      "Gender Split (M/F)",
      "Top Age Group",
    ];
    const rows = creatorDetails.map((detail, idx) => {
      const cName = detail?.creator?.creator || `Creator #${idx + 1}`;
      const location = detail?.creator?.geolocation || detail?.creator?.region || "N/A";
      const mainPlatform = findMainPlatform(detail?.creator?.public);
      const platformName = mainPlatform?.platform?.name || "N/A";
      const followers = mainPlatform?.followers || 0;
      const avgLikes = mainPlatform?.avg_likes || 0;
      const avgComments = mainPlatform?.avg_comments || 0;
      const engagementRate = followers ? (((avgLikes + avgComments) / followers) * 100).toFixed(2) : 0;
      const { topCountry, topAge, malePercent, femalePercent } = computePlatformSegments(mainPlatform);
      return [
        `"${cName}"`,
        `"${location}"`,
        `"${platformName}"`,
        followers,
        avgLikes,
        avgComments,
        `${engagementRate}%`,
        `"${topCountry.code} (${topCountry.value.toFixed(0)}%)"`,
        `"${malePercent}% / ${femalePercent}%"`,
        `"${topAge.name} (${topAge.value.toFixed(0)}%)"`,
      ].join(",");
    });
    return [headers.join(","), ...rows].join("\n");
  };

  const exportCreatorBreakdownCSV = () => {
    const csvContent = convertCreatorDetailsToCSV();
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `campaign_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setExportMenuAnchor(null);
  };

  const exportCreatorBreakdownSheets = async () => {
    try {
      const response = await client.campaigns.exportSheetCreators({ data: creatorDetails });
      if (response.sheetLink) {
        window.open(response.sheetLink, "_blank");
      }
    } catch (error) {
      console.error("Failed to export to Google Sheets:", error);
    }
    setExportMenuAnchor(null);
  };

  return (
    <StyledContainer maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 6 }}>
        <PageTitle variant="h1">Campaign Performance Report</PageTitle>
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            color: "#718096",
            fontWeight: 500,
            mb: 3,
          }}
        >
          Real-time insights and analytics for your influencer campaign
        </Typography>
        
        <Box display="flex" justifyContent="center" gap={2}>
          <GradientButton
            startIcon={<DownloadIcon />}
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
          >
            Export Report
          </GradientButton>
        </Box>
        
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={() => setExportMenuAnchor(null)}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              mt: 1,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          <MenuItem onClick={exportCreatorBreakdownCSV} sx={{ py: 1.5, px: 3 }}>
            <DownloadIcon sx={{ mr: 1.5, color: "#667eea" }} />
            Export as CSV
          </MenuItem>
          <MenuItem onClick={exportCreatorBreakdownSheets} sx={{ py: 1.5, px: 3 }}>
            <PublicIcon sx={{ mr: 1.5, color: "#43e97b" }} />
            Export to Google Sheets
          </MenuItem>
        </Menu>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        </Box>
      )}

      {!loading && (
        <>
          {/* Key Metrics Overview */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard gradient={METRIC_GRADIENTS.purple}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ bgcolor: "rgba(255, 255, 255, 0.2)", mr: 2 }}>
                    <MoneyIcon sx={{ color: "#fff" }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 600 }}>
                    Total Investment
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: "#fff" }}>
                  ${summaryMetrics.totalCost.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.8)", mt: 1 }}>
                  Avg ${summaryMetrics.avgCostPerCreator.toLocaleString()} per creator
                </Typography>
              </MetricCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <MetricCard gradient={METRIC_GRADIENTS.blue}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ bgcolor: "rgba(255, 255, 255, 0.2)", mr: 2 }}>
                    <ViewsIcon sx={{ color: "#fff" }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 600 }}>
                    Total Views
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: "#fff" }}>
                  {summaryMetrics.totalViews.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.8)", mt: 1 }}>
                  Across all content
                </Typography>
              </MetricCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <MetricCard gradient={METRIC_GRADIENTS.green}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ bgcolor: "rgba(255, 255, 255, 0.2)", mr: 2 }}>
                    <PeopleIcon sx={{ color: "#fff" }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 600 }}>
                    Total Reach
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: "#fff" }}>
                  {summaryMetrics.totalReach.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.8)", mt: 1 }}>
                  Unique audience members
                </Typography>
              </MetricCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <MetricCard gradient={METRIC_GRADIENTS.pink}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ bgcolor: "rgba(255, 255, 255, 0.2)", mr: 2 }}>
                    <TrendingUpIcon sx={{ color: "#fff" }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 600 }}>
                    Active Creators
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: "#fff" }}>
                  {creatorDetails.length}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.8)", mt: 1 }}>
                  In this campaign
                </Typography>
              </MetricCard>
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {/* Cost Per Creator */}
            <Grid item xs={12} lg={6}>
              <ModernCard>
                <Box sx={{ p: 3 }}>
                  <SectionTitle>
                    <BarChartIcon sx={{ color: "#667eea" }} />
                    Investment Distribution
                  </SectionTitle>
                  <ChartContainer>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={costPerCreatorData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar 
                          dataKey="price" 
                          fill="url(#costGradient)" 
                          radius={[8, 8, 0, 0]}
                        />
                        <defs>
                          <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#667eea" />
                            <stop offset="100%" stopColor="#764ba2" />
                          </linearGradient>
                        </defs>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </Box>
              </ModernCard>
            </Grid>

            {/* Views Over Time */}
            <Grid item xs={12} lg={6}>
              <ModernCard>
                <Box sx={{ p: 3 }}>
                  <SectionTitle>
                    <TrendingUpIcon sx={{ color: "#43e97b" }} />
                    Views Trend
                  </SectionTitle>
                  <ChartContainer>
                    <ResponsiveContainer width="100%" height={350}>
                      <AreaChart data={viewsByDayData}>
                        <defs>
                          <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#43e97b" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#38f9d7" stopOpacity={0.1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                          type="monotone" 
                          dataKey="views" 
                          stroke="#43e97b" 
                          strokeWidth={3}
                          fill="url(#viewsGradient)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </Box>
              </ModernCard>
            </Grid>

            {/* Reach Distribution */}
            <Grid item xs={12} md={6}>
              <ModernCard>
                <Box sx={{ p: 3 }}>
                  <SectionTitle>
                    <PeopleIcon sx={{ color: "#f093fb" }} />
                    Reach by Creator
                  </SectionTitle>
                  <ChartContainer>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={reachData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius="70%"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {reachData.map((entry, idx) => (
                            <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </Box>
              </ModernCard>
            </Grid>

            {/* Performance Summary */}
            <Grid item xs={12} md={6}>
              <ModernCard>
                <Box sx={{ p: 3 }}>
                  <SectionTitle>
                    <BarChartIcon sx={{ color: "#4facfe" }} />
                    Top Performers
                  </SectionTitle>
                  <Box sx={{ mt: 2 }}>
                    {reachData.slice(0, 5).map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 2,
                          mb: 2,
                          borderRadius: "12px",
                          background: `linear-gradient(90deg, ${CHART_COLORS[idx % CHART_COLORS.length]}15, transparent)`,
                          border: `1px solid ${CHART_COLORS[idx % CHART_COLORS.length]}30`,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateX(8px)",
                            boxShadow: `0 4px 16px ${CHART_COLORS[idx % CHART_COLORS.length]}20`,
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar
                            sx={{
                              bgcolor: CHART_COLORS[idx % CHART_COLORS.length],
                              fontWeight: 700,
                            }}
                          >
                            {idx + 1}
                          </Avatar>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {item.name}
                          </Typography>
                        </Box>
                        <StatBadge
                          label={item.value.toLocaleString()}
                          color={CHART_COLORS[idx % CHART_COLORS.length]}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </ModernCard>
            </Grid>
          </Grid>

          {/* Creator Details Table */}
          {creatorDetails.length > 0 && (
            <ModernCard sx={{ mb: 4 }}>
              <Box sx={{ p: 3 }}>
                <SectionTitle>
                  <PeopleIcon sx={{ color: "#667eea" }} />
                  Creator Analytics Breakdown
                </SectionTitle>
                
                <Box sx={{ overflowX: "auto", borderRadius: "16px" }}>
                  <StyledTable>
                    <TableHead>
                      <TableRow>
                        <TableCell>Creator</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Platform</TableCell>
                        <TableCell align="right">Followers</TableCell>
                        <TableCell align="right">Avg Likes</TableCell>
                        <TableCell align="right">Avg Comments</TableCell>
                        <TableCell align="right">Engagement</TableCell>
                        <TableCell>Top Country</TableCell>
                        <TableCell>Gender Split</TableCell>
                        <TableCell>Top Age</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {creatorDetails.map((detail, index) => {
                        const creatorName = detail?.creator?.creator || `Creator #${index + 1}`;
                        const location = detail?.creator?.geolocation || detail?.creator?.region || "N/A";
                        const mainPlatform = findMainPlatform(detail?.creator?.public);
                        const platformName = mainPlatform?.platform?.name || "N/A";
                        const followers = mainPlatform?.followers || 0;
                        const avgLikes = mainPlatform?.avg_likes || 0;
                        const avgComments = mainPlatform?.avg_comments || 0;
                        const engagementRate = followers 
                          ? (((avgLikes + avgComments) / followers) * 100).toFixed(2) 
                          : 0;
                        const { topCountry, topAge, malePercent, femalePercent } =
                          computePlatformSegments(mainPlatform);

                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Avatar 
                                  sx={{ 
                                    bgcolor: CHART_COLORS[index % CHART_COLORS.length],
                                    width: 32,
                                    height: 32,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {creatorName.charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {creatorName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{location}</TableCell>
                            <TableCell>
                              <Chip 
                                label={platformName} 
                                size="small"
                                sx={{ 
                                  bgcolor: "rgba(102, 126, 234, 0.1)",
                                  color: "#667eea",
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {followers.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{avgLikes.toLocaleString()}</TableCell>
                            <TableCell align="right">{avgComments.toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${engagementRate}%`}
                                size="small"
                                sx={{
                                  bgcolor: engagementRate > 3 
                                    ? "rgba(67, 233, 123, 0.2)" 
                                    : "rgba(255, 154, 86, 0.2)",
                                  color: engagementRate > 3 ? "#43e97b" : "#ff9a56",
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {topCountry.code} ({topCountry.value.toFixed(0)}%)
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {malePercent}% / {femalePercent}%
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {topAge.name} ({topAge.value.toFixed(0)}%)
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </StyledTable>
                </Box>
              </Box>
            </ModernCard>
          )}
        </>
      )}
    </StyledContainer>
  );
};

export default CampaignReportingPanel;